/**
 * AutoElite — Professional PDF Invoice Generator (Customer App)
 * Clean white background, black text — crisp professional look.
 * Uses jsPDF Helvetica. Rs. prefix (jsPDF can't render ₹ Unicode).
 */
import { jsPDF } from 'jspdf';

const rs = n => 'Rs. ' + Number(n || 0).toLocaleString('en-IN');

/* ── Colour palette — black on white ─────────────────────────────────────── */
const C = {
  black:     [0,   0,   0  ],
  darkText:  [20,  20,  20 ],
  midText:   [60,  60,  60 ],
  mutedText: [110, 110, 110],
  accent:    [0,   82,  204],   // blue for invoice ID / totals header
  accentBg:  [0,   82,  204],
  white:     [255, 255, 255],
  offWhite:  [248, 248, 248],
  rowAlt:    [245, 247, 250],
  border:    [200, 200, 200],
  borderDark:[140, 140, 140],
  success:   [22,  130, 60 ],
  successBg: [235, 252, 240],
  dangerBg:  [254, 242, 242],
  headerBg:  [15,  15,  15 ],   // near-black header
  amberBg:   [255, 251, 235],
  amber:     [146, 86,  0  ],
};

/* ── Drawing helpers ─────────────────────────────────────────────────────── */
const fill = (doc, x, y, w, h, rgb) => {
  doc.setFillColor(...rgb); doc.rect(x, y, w, h, 'F');
};
const outline = (doc, x, y, w, h, rgb = C.border, lw = 0.3) => {
  doc.setDrawColor(...rgb); doc.setLineWidth(lw); doc.rect(x, y, w, h, 'S');
};
const hline = (doc, x1, x2, y, rgb = C.border, lw = 0.25) => {
  doc.setDrawColor(...rgb); doc.setLineWidth(lw); doc.line(x1, y, x2, y);
};
const t = (doc, text, x, y, opts = {}) => {
  const { size = 9, bold = false, italic = false,
          color = C.black, align = 'left', maxW } = opts;
  doc.setFont('helvetica',
    bold && italic ? 'bolditalic' : bold ? 'bold' : italic ? 'italic' : 'normal');
  doc.setFontSize(size);
  doc.setTextColor(...color);
  const o = { align };
  if (maxW) o.maxWidth = maxW;
  doc.text(String(text ?? ''), x, y, o);
};

/* ── Calculations ────────────────────────────────────────────────────────── */
function calc(rec) {
  const subtotal  = rec.subtotal  || rec.amount || 0;
  const gst       = rec.gstPercent ?? rec.gst ?? 18;
  const partsTotal= (rec.spareParts||[]).reduce((s,p)=>s+Number(p.lineTotal||p.qty*(p.unitPrice||0)||0),0);
  const slBase    = subtotal - partsTotal;
  const partsGst  = Math.round(partsTotal * 18 / 100);
  const svcsGst   = Math.round(slBase * gst / 100);
  const gstAmt    = rec.gstAmount || (partsGst + svcsGst);
  const grand     = rec.grandTotal || subtotal + gstAmt;
  return { subtotal, gst, gstAmt, grand, partsTotal, partsGst, svcsGst, slBase };
}

/* 
   MAIN BUILDER
 */
export function buildInvoicePdf(rec, user) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const PW = 210, PH = 297, ML = 15, MR = PW - ML, CW = MR - ML;
  const { subtotal, gst, grand, partsTotal, partsGst, svcsGst } = calc(rec);
  let y = 0;

  /* 1. HEADER */
  fill(doc, 0, 0, PW, 46, C.white);

  // Brand name
  t(doc, 'AutoElite', ML, 15, { size: 22, bold: true, color: C.black });
  t(doc, 'CERTIFIED VEHICLE SERVICE CENTRE',
    ML, 22, { size: 9, color: [0,0,0] });
  t(doc, '#24, Kissan Market, Sirhind Road, Patiala, Punjab 147001',
    ML, 28.5, { size: 8, color: [0,0,0] });
  t(doc, '+91 77194 12031   |   Gurkirat@autoelite.in',
    ML, 34, { size: 8, color: [0,0,0] });
  t(doc, 'GSTIN: 03AABCU9603R1ZV   |   State: Punjab (03)',
    ML, 39.5, { size: 8, color: [0,0,0] });

  // Right side — invoice details
  const invNo   = rec.id || rec.invoiceId || 'INV-0000';
  const invDate = rec.serviceDate
    ? String(rec.serviceDate)
    : rec.date ? String(rec.date)
    : new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  t(doc, 'TAX INVOICE', MR, 13, { size: 8, bold: true, color: [0,0,0], align: 'right' });
  t(doc, invNo,         MR, 21, { size: 15, bold: true, color: C.black, align: 'right' });
  t(doc, `Date: ${invDate}`,
    MR, 28, { size: 9, color: [0,0,0], align: 'right' });

  // Status pill
  const paid    = rec.invoicePaid || rec.paid || rec.status === 'paid' || rec.invoiceStatus === 'paid';
  const pillW   = 28;
  fill(doc, MR - pillW, 31, pillW, 8, paid ? C.success : [255,255,255]);
  t(doc, paid ? 'PAID' : 'PENDING',
    MR - pillW / 2, 36.5, { size: 7, bold: true, color: C.black, align: 'center' });

  y = 52;

  /*2. BILL TO + VEHICLE*/
  const halfW = (CW - 5) / 2, cardH = 40;

  // Bill To
  fill(doc, ML, y, halfW, cardH, C.white);
  outline(doc, ML, y, halfW, cardH, C.black, 0.2);
  hline(doc, ML, ML + halfW, y + 7, C.black, 0.4);
  t(doc, 'BILL TO', ML + 4, y + 5, { size: 10, bold: true, color: C.black });
  t(doc, user?.name || rec.customerName || 'Customer',
    ML + 4, y + 14, { size: 10, bold: true, color: C.black });
  t(doc, user?.phone || rec.customerPhone || '—',
    ML + 4, y + 21, { size: 9, color: C.black });
  t(doc, user?.email || rec.customerEmail || '—',
    ML + 4, y + 28, { size: 9, color: C.black });

  // Vehicle
  const bx = ML + halfW + 5;
  fill(doc, bx, y, halfW, cardH, C.white);
  outline(doc, bx, y, halfW, cardH, C.black, 0.2);
  hline(doc, bx, bx + halfW, y + 7, C.black, 0.4);
  t(doc, 'VEHICLE DETAILS', bx + 4, y + 5, { size: 10, bold: true, color: C.black });
  t(doc, rec.vehicleDisplay || rec.vehicle || '—',
    bx + 4, y + 14, { size: 10, bold: true, color: C.black });
  t(doc, `Reg: ${rec.regNumber || rec.regNo || '—'}`,
    bx + 4, y + 21, { size: 9, color: C.black });
  if (rec.fuelType)
    t(doc, `Fuel: ${rec.fuelType}`, bx + 4, y + 28, { size: 9, color: C.black });
  if (rec.mechanicName || rec.mechanic)
    t(doc, `Mechanic: ${rec.mechanicName || rec.mechanic}`,
      bx + 4, y + (rec.fuelType ? 34 : 28), { size: 9, color: C.black });

  y += cardH + 8;

  /* 3. TABLE */
  const COL  = { desc: ML, qty: ML + 96, rate: ML + 128, amt: MR };
  const TH_H = 9;

  // Table header — dark
  fill(doc, ML, y, CW, TH_H, C.white);
  t(doc, 'DESCRIPTION', COL.desc + 4, y + 6.2, { size: 10, bold: true, color: C.black });
  t(doc, 'QTY',   COL.qty,  y + 6.2, { size: 10, bold: true, color: C.black, align: 'center' });
  t(doc, 'RATE',  COL.rate, y + 6.2, { size: 10, bold: true, color: C.black, align: 'right' });
  t(doc, 'AMOUNT',COL.amt,  y + 6.2, { size: 10, bold: true, color: C.black, align: 'right' });
  y += TH_H;
  hline(doc, ML, MR, y, C.black, 0.5);

  let altRow = false;
  const drawRow = (desc, type, qty, rate, amount) => {
    const ROW_H = 9;
    fill(doc, ML, y, CW, ROW_H, altRow ? C.white : C.white);
    altRow = !altRow;

    // tiny type label
    const typeColors = { Service: C.accent, Part: [16, 110, 50], Labour: C.amber };
    const tc = typeColors[type] || C.mutedText;
    t(doc, type, COL.desc + 4, y + 6, { size: 6.5, bold: true, color: tc });

    t(doc, desc,         COL.desc + 22, y + 6, { size: 9, color: C.black });
    t(doc, String(qty),  COL.qty,       y + 6, { size: 9, color: C.black, align: 'center' });
    t(doc, String(rate), COL.rate,      y + 6, { size: 9, color: C.black, align: 'right' });
    t(doc, amount,       COL.amt,       y + 6, { size: 9, bold: true, color: C.black, align: 'right' });

    hline(doc, ML, MR, y + ROW_H);
    y += ROW_H;
  };

  (rec.services || []).forEach(s => {
    const name = typeof s === 'string' ? s : s.name;
    const amt  = typeof s === 'object' ? s.unitPrice || s.amount : null;
    drawRow(name, 'Service', 1, amt ? rs(amt) : '—', amt ? rs(amt) : '—');
  });

  (rec.spareParts || []).forEach(p =>
    drawRow(p.name, 'Part', p.qty,
      rs(p.unitPrice || p.rate || 0),
      rs(p.lineTotal || p.qty * (p.unitPrice || p.rate || 0))));

  const lc = Number(rec.labourCharges || rec.labour || 0);
  if (lc > 0) drawRow('Labour Charges', 'Labour', 1, rs(lc), rs(lc));

  // Table bottom border
  hline(doc, ML, MR, y, C.black, 0.5);
  y += 8;

  /*  4. TOTALS  */
  const TW = 80, TX = MR - TW;

  // Subtotal
  t(doc, 'Subtotal',    TX + 4, y + 7, { size: 10, color: C.black});
  t(doc, rs(subtotal),  MR - 2, y + 7, { size: 10, bold: true, color: C.black, align: 'right' });
  hline(doc, TX, MR, y + 10, C.black,0.2);

  // GST — split into services/labour and spare parts
  t(doc, `GST on Services & Labour @ ${gst}%`, TX + 4, y + 19, { size: 9, color: C.gray || C.black });
  t(doc, rs(svcsGst), MR - 2, y + 19, { size: 9, bold: true, color: C.black, align: 'right' });
  if (partsTotal > 0) {
    y += 14;
    t(doc, 'GST on Spare Parts @ 18%', TX + 4, y + 19, { size: 9, color: C.gray || C.black });
    t(doc, rs(partsGst), MR - 2, y + 19, { size: 9, bold: true, color: C.black, align: 'right' });
  }
  hline(doc, TX, MR, y + 22, C.black, 1);

  // Grand total — dark band
  fill(doc, TX, y + 22, TW, 12, C.white);
  t(doc, 'GRAND TOTAL', TX + 4, y + 30, { size: 10, bold: true, color: C.black });
  t(doc, rs(grand),     MR - 2, y + 30, { size: 10, bold: true, color: C.black, align: 'right' });

  y += 40;

  /*  5. AMOUNT IN WORDS  */
  fill(doc, ML, y, CW, 10, C.white);
  outline(doc, ML, y, CW, 10, C.black, 0.2);
  t(doc, 'Amount in Words:', ML + 4, y + 7, { size: 10, bold: true, color: C.black });
  t(doc, amountInWords(grand) + ' Only',
    ML + 43, y + 7, { size: 9, italic: true, color: C.black });
  y += 14;

  /*6. NOTES  */
  if (rec.notes && rec.notes.trim()) {
    fill(doc, ML, y, CW, 20, C.white);
    outline(doc, ML, y, CW, 20, C.black, 0.2);
    doc.setDrawColor(...C.black); doc.setLineWidth(1.2);
    doc.line(ML, y, ML, y + 20);
    t(doc, 'MECHANIC NOTES', ML + 6, y + 6.5, { size: 10, bold: true, color: C.black });
    const nl = doc.splitTextToSize(rec.notes, CW - 14);
    nl.slice(0, 2).forEach((ln, i) =>
      t(doc, ln, ML + 6, y + 13 + i * 5, { size: 9, color: C.black }));
    y += 24;
  }

  /* ━━━ 7. PAYMENT INFO  */
  if (paid) {
    const paidDateStr = rec.paidAt
      ? new Date(rec.paidAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
      : invDate;
    fill(doc, ML, y, CW, 10, C.white);
    outline(doc, ML, y, CW, 10, [0,0,0], 0.2);
    t(doc, `PAYMENT RECEIVED  |  Cleared: ${paidDateStr}`,
      PW / 2, y + 7, { size: 7.5, bold: true, color: C.black, align: 'center' });
    y += 14;
  }

  /* ━━━ 8. TERMS */
  t(doc, 'Terms & Conditions', ML, y + 6, { size: 10, bold: true, color: C.black });
  t(doc, '1. Payment due within 7 days of service.   2. Parts replaced carry 30-day warranty.   3. Disputes subject to Patiala jurisdiction.',
    ML, y + 12, { size: 9, color: C.black });

  /* ━━━ 9. WATERMARK  */
  if (!paid) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(62);
    doc.setTextColor(200, 0, 0);
    doc.setGState(new doc.GState({ opacity: 0.06 }));
    doc.text('UNPAID', PW / 2, PH / 2 + 10, { align: 'center', angle: 38 });
    doc.setGState(new doc.GState({ opacity: 1 }));
  }

  /* ━━━ 10. FOOTER  */
  hline(doc, ML, MR, PH - 20, C.borderDark, 0.5);
  t(doc, 'Thank you for choosing AutoElite — Certified Workshop | ISO 9001:2015',
    PW / 2, PH - 14, { size: 10, bold: true, color: C.black, align: 'center' });
  t(doc, `Computer generated invoice — no signature required  |  Generated: ${new Date().toLocaleString('en-IN')}`,
    PW / 2, PH - 8, { size: 9, color: C.black, align: 'center' });

  return doc;
}

/* ── Amount in words */
function amountInWords(n) {
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
                 'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen',
                 'Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  function w(num) {
    if (!num) return '';
    if (num < 20)     return ones[num] + ' ';
    if (num < 100)    return tens[Math.floor(num / 10)] + ' ' + (ones[num % 10] ? ones[num % 10] + ' ' : '');
    if (num < 1000)   return ones[Math.floor(num / 100)] + ' Hundred ' + w(num % 100);
    if (num < 100000) return w(Math.floor(num / 1000)) + 'Thousand ' + w(num % 1000);
    if (num < 1e7)    return w(Math.floor(num / 100000)) + 'Lakh ' + w(num % 100000);
    return w(Math.floor(num / 1e7)) + 'Crore ' + w(num % 1e7);
  }
  const rupees = Math.floor(n);
  const paise  = Math.round((n - rupees) * 100);
  let result   = 'Rupees ' + (w(rupees).trim() || 'Zero');
  if (paise > 0) result += ' and ' + w(paise).trim() + ' Paise';
  return result;
}

/* ── Public API */
export function downloadInvoicePdf(rec, user) {
  const doc = buildInvoicePdf(rec, user);
  doc.save(`AutoElite_${rec.id || rec.invoiceId || 'Invoice'}.pdf`);
}

export function printInvoicePdf(rec, user) {
  const doc  = buildInvoicePdf(rec, user);
  const blob = doc.output('blob');
  const url  = URL.createObjectURL(blob);
  const win  = window.open(url, '_blank');
  if (win) setTimeout(() => { try { win.focus(); win.print(); } catch {} }, 1200);
  setTimeout(() => URL.revokeObjectURL(url), 120_000);
}